import {
  ArrowDownLeftIcon,
  ArrowSquareOutIcon,
  ArrowsLeftRightIcon,
  ArrowUpRightIcon,
  ChartLineUpIcon,
  CoinIcon,
  CopyIcon,
  GearIcon,
  HouseIcon,
  QuestionIcon,
  RankingIcon,
  ReceiptIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  StorefrontIcon,
  UserPlusIcon,
  UsersThreeIcon,
  WalletIcon,
  WarningIcon,
} from '@phosphor-icons/react/dist/ssr';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '../auth';
import { useT, type TFunction } from '../i18n';
import { hasApprovalsDone } from '../lib/approvals-flag';
import {
  getForeignBalances,
  getWalletBalance,
  type ForeignBalancesResponse,
  type ForeignTokenBalance,
  type WalletBalanceResponse,
} from '../api';

const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

type Tone = 'primary' | 'info' | 'warning' | 'danger' | 'success' | 'violet';

type Tile = {
  to: string;
  titleKey: string;
  descKey: string;
  Icon: typeof StorefrontIcon;
  tone: Tone;
};

const BROWSE_TILES: Tile[] = [
  {
    to: '/markets',
    titleKey: 'home.tile.markets_title',
    descKey: 'home.tile.markets_desc',
    Icon: StorefrontIcon,
    tone: 'primary',
  },
  {
    to: '/portfolio',
    titleKey: 'home.tile.portfolio_title',
    descKey: 'home.tile.portfolio_desc',
    Icon: ChartLineUpIcon,
    tone: 'success',
  },
  {
    to: '/orders',
    titleKey: 'home.tile.orders_title',
    descKey: 'home.tile.orders_desc',
    Icon: ReceiptIcon,
    tone: 'violet',
  },
];

const SOCIAL_TILES: Tile[] = [
  {
    to: '/ranking',
    titleKey: 'home.tile.ranking_title',
    descKey: 'home.tile.ranking_desc',
    Icon: RankingIcon,
    tone: 'warning',
  },
  {
    to: '/copy',
    titleKey: 'home.tile.copy_title',
    descKey: 'home.tile.copy_desc',
    Icon: UserPlusIcon,
    tone: 'info',
  },
];

const MONEY_TILES: Tile[] = [
  {
    to: '/deposit',
    titleKey: 'home.tile.deposit_title',
    descKey: 'home.tile.deposit_desc',
    Icon: ArrowDownLeftIcon,
    tone: 'primary',
  },
  {
    to: '/withdraw',
    titleKey: 'home.tile.withdraw_title',
    descKey: 'home.tile.withdraw_desc',
    Icon: ArrowUpRightIcon,
    tone: 'info',
  },
  {
    to: '/swap',
    titleKey: 'home.tile.swap_title',
    descKey: 'home.tile.swap_desc',
    Icon: ArrowsLeftRightIcon,
    tone: 'warning',
  },
  {
    to: '/bridge',
    titleKey: 'home.tile.bridge_title',
    descKey: 'home.tile.bridge_desc',
    Icon: CoinIcon,
    tone: 'info',
  },
];

const TONE_CLASSES: Record<Tone, string> = {
  primary:
    'bg-primary/15 text-foreground ring-primary/25 group-hover:bg-primary/25',
  info: 'bg-sky-500/12 text-sky-300 ring-sky-500/25 group-hover:bg-sky-500/20',
  warning:
    'bg-amber-500/12 text-amber-300 ring-amber-500/25 group-hover:bg-amber-500/20',
  danger:
    'bg-rose-500/12 text-rose-300 ring-rose-500/25 group-hover:bg-rose-500/20',
  success:
    'bg-emerald-500/12 text-emerald-300 ring-emerald-500/25 group-hover:bg-emerald-500/20',
  violet:
    'bg-violet-500/15 text-violet-200 ring-violet-500/30 group-hover:bg-violet-500/25',
};

const BALANCE_POLL_MS = 30_000;
const FOREIGN_POLL_MS = 30_000;

function fmtUsd(s: string | null | undefined): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function fmtForeign(token: ForeignTokenBalance): string {
  const n = Number(token.balance);
  if (!Number.isFinite(n)) return token.balance;
  return token.decimals >= 18 ? n.toFixed(4) : n.toFixed(2);
}

function ForeignFundsAlert({ data, t }: { data: ForeignBalancesResponse; t: TFunction }) {
  const present = data.tokens.filter((tk) => tk.hasBalance);
  if (present.length === 0) return null;
  return (
    <Alert tone="warning">
      <WarningIcon weight="fill" className="size-4" />
      <AlertTitle>{t('home.foreign.title')}</AlertTitle>
      <AlertDescription>
        <p
          className="mb-2"
          dangerouslySetInnerHTML={{ __html: t('home.foreign.body') }}
        />
        <p className="text-muted-foreground text-[11px] font-semibold tracking-[0.14em] uppercase">
          {t('home.foreign.list_label')}
        </p>
        <ul className="my-2 flex flex-col gap-1 font-mono text-[13px]">
          {present.map((tk) => (
            <li key={tk.symbol} className="tabular-nums">
              <strong>{fmtForeign(tk)}</strong> {tk.symbol}
            </li>
          ))}
        </ul>
        <p className="mb-3 text-[12px] opacity-90">{t('home.foreign.cta')}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Link
            to="/rescue/pusd"
            className={cn(
              buttonVariants({ size: 'sm' }),
              'flex-1 justify-center',
            )}
          >
            {t('home.foreign.cta_pusd')}
          </Link>
          <Link
            to="/rescue/eoa"
            className={cn(
              buttonVariants({ variant: 'outline', size: 'sm' }),
              'flex-1 justify-center',
            )}
          >
            {t('home.foreign.cta_eoa')}
          </Link>
        </div>
      </AlertDescription>
    </Alert>
  );
}

function useForeignBalances(externalUserId: string | null) {
  const [data, setData] = useState<ForeignBalancesResponse | null>(null);
  useEffect(() => {
    if (!externalUserId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getForeignBalances(externalUserId);
        if (!cancelled) setData(res);
      } catch {
        // alerta nunca deve quebrar o Home
      }
    };
    void load();
    const id = window.setInterval(load, FOREIGN_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [externalUserId]);
  return data;
}

function useWalletBalance(externalUserId: string | null) {
  const [data, setData] = useState<WalletBalanceResponse | null>(null);
  useEffect(() => {
    if (!externalUserId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getWalletBalance(externalUserId);
        if (!cancelled) setData(res);
      } catch {
        // saldo é informativo; falha silenciosa
      }
    };
    void load();
    const id = window.setInterval(load, BALANCE_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [externalUserId]);
  return data;
}

function copyToClipboard(text: string): void {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    void navigator.clipboard.writeText(text);
  }
}

function TileCard({ tile, t }: { tile: Tile; t: TFunction }) {
  return (
    <Link
      to={tile.to}
      className="group bg-card/90 supports-backdrop-filter:bg-card/85 ring-foreground/10 hover:ring-foreground/25 hover:bg-card relative flex flex-col gap-3 overflow-hidden rounded-3xl p-4 ring-1 backdrop-blur-md transition-all hover:-translate-y-0.5 sm:p-5"
    >
      <span
        aria-hidden
        className="from-primary/40 pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r via-violet-500/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100"
      />
      <span
        className={cn(
          'inline-flex size-10 items-center justify-center rounded-2xl ring-1 transition-colors',
          TONE_CLASSES[tile.tone],
        )}
      >
        <tile.Icon weight="fill" className="size-5" />
      </span>
      <div className="flex flex-col gap-1">
        <span className="text-foreground text-[15px] font-semibold tracking-tight">
          {t(tile.titleKey)}
        </span>
        <span className="text-muted-foreground text-xs leading-relaxed">
          {t(tile.descKey)}
        </span>
      </div>
    </Link>
  );
}

export default function Home() {
  const auth = useAuth();
  const t = useT();
  const externalUserId =
    auth.state === 'ready' ? auth.auth.externalUserId : null;
  const foreign = useForeignBalances(externalUserId);
  const balance = useWalletBalance(externalUserId);

  if (auth.state === 'loading') {
    return (
      <AuthLoadingScreen
        title={t('home.loading_title')}
        description={t('home.loading_desc')}
        shortDescription={t('home.loading_short')}
      />
    );
  }

  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow={t('home.auth_eyebrow')} title={t('home.title')}>
        <Alert tone="danger">
          <AlertTitle>
            {t('common.auth_error', { error: '' }) || t('home.auth_failed')}
          </AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  const depositAddress = auth.auth.depositWalletAddress ?? auth.address;
  const isDeployed = balance?.isDeployed ?? true;
  const approvalsDone = hasApprovalsDone(depositAddress);

  return (
    <ScreenShell
      eyebrow={
        <span className="inline-flex items-center gap-1.5">
          <HouseIcon weight="fill" className="size-3" />
          {t('home.title')}
        </span>
      }
      title={t('home.title')}
      description={t('home.ready')}
    >
      <Card>
        <CardHeader>
          <CardEyebrow>
            <WalletIcon weight="fill" className="size-3" />
            {t('home.balance_title')}
          </CardEyebrow>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="font-mono text-2xl sm:text-3xl tabular-nums">
              {balance ? fmtUsd(balance.pUsdBalance) : t('home.balance_loading')}
            </CardTitle>
            <Badge tone={isDeployed ? 'success' : 'warning'}>
              <span
                className={cn(
                  'auth-loading-dot',
                  isDeployed ? 'bg-emerald-500' : 'bg-amber-500',
                )}
              />
              {short(depositAddress)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => copyToClipboard(depositAddress)}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 self-start text-[12px] font-medium tabular-nums transition-colors"
          >
            <CopyIcon weight="bold" className="size-3" />
            {t('common.copy_address')}
          </button>
          {!isDeployed ? (
            <Link
              to="/deploy-wallet"
              className={cn(
                buttonVariants({ variant: 'outline', size: 'sm' }),
                'self-start',
              )}
            >
              <RocketLaunchIcon weight="bold" className="size-4" />
              {t('home.deploy_wallet_cta')}
            </Link>
          ) : null}
        </CardContent>
      </Card>

      {foreign && <ForeignFundsAlert data={foreign} t={t} />}

      <div className="flex flex-col gap-2">
        <CardEyebrow className="pl-1">
          <StorefrontIcon weight="fill" className="size-3" />
          {t('home.section_browse')}
        </CardEyebrow>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {BROWSE_TILES.map((tile) => (
            <TileCard key={tile.to} tile={tile} t={t} />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <CardEyebrow className="pl-1">
          <UsersThreeIcon weight="fill" className="size-3" />
          {t('home.section_social')}
        </CardEyebrow>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {SOCIAL_TILES.map((tile) => (
            <TileCard key={tile.to} tile={tile} t={t} />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <CardEyebrow className="pl-1">
          <CoinIcon weight="fill" className="size-3" />
          {t('home.section_money')}
        </CardEyebrow>
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {MONEY_TILES.map((tile) => (
            <TileCard key={tile.to} tile={tile} t={t} />
          ))}
        </div>
      </div>

      <Link
        to="/approvals"
        className={cn(
          'group bg-card/85 ring-foreground/10 hover:ring-rose-500/30 flex items-center gap-3 rounded-2xl p-3 ring-1 transition-all',
          approvalsDone && 'hidden',
        )}
      >
        <span
          className={cn(
            'inline-flex size-9 items-center justify-center rounded-xl ring-1',
            TONE_CLASSES.danger,
          )}
        >
          <ShieldCheckIcon weight="fill" className="size-4" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-foreground text-[14px] font-semibold">
            {t('home.tile.approvals_title')}
          </span>
          <span className="text-muted-foreground text-[12px]">
            {t('home.tile.approvals_desc')}
          </span>
        </div>
        <ArrowSquareOutIcon
          weight="bold"
          className="text-muted-foreground size-4 opacity-60 transition-opacity group-hover:opacity-100"
          aria-hidden
        />
      </Link>

      <div className="grid grid-cols-2 gap-2">
        <Link
          to="/help"
          className="group bg-card/80 ring-foreground/10 hover:ring-foreground/25 flex items-center justify-center gap-2 rounded-2xl px-3 py-2.5 ring-1 transition-all"
        >
          <QuestionIcon weight="fill" className="text-muted-foreground size-4 group-hover:text-foreground" />
          <span className="text-muted-foreground group-hover:text-foreground text-[13px] font-semibold">
            {t('help.eyebrow')}
          </span>
        </Link>
        <Link
          to="/settings"
          className="group bg-card/80 ring-foreground/10 hover:ring-foreground/25 flex items-center justify-center gap-2 rounded-2xl px-3 py-2.5 ring-1 transition-all"
        >
          <GearIcon weight="fill" className="text-muted-foreground size-4 group-hover:text-foreground" />
          <span className="text-muted-foreground group-hover:text-foreground text-[13px] font-semibold">
            {t('settings.eyebrow')}
          </span>
        </Link>
      </div>
    </ScreenShell>
  );
}

